import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TalkDebate, TalkContribution, useTalkStore } from "@/data/talkStore";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { talkLocales, TalkLocales } from "@/i18n/talkLocales";
import { useToast } from "@/hooks/use-toast";
import AuthModal from "./AuthModal";
import { Link } from "react-router-dom";
import { Users, Presentation, Shield, Building2, MessageCircle, Mic, ThumbsUp, ThumbsDown, ArrowRight, X, AlertCircle, CheckCircle2 } from "lucide-react";
const CatIcon = {
  CITOYEN: Users,
  ONG: Presentation,
  PARTI: Shield,
  INSTITUTION: Building2
};

export default function TalkSection() {
  const { lang, isRtl } = useI18n();
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  
  const t = talkLocales[lang as TalkLocales] || talkLocales.fr;
  
  const {
    debates: allDebates,
    contributions: allContributions,
    interactions,
    fetchDebates,
    fetchContributions,
    submitContribution,
    toggleTalkContributionInteraction
  } = useTalkStore();

  const [activeDebate, setActiveDebate] = useState<TalkDebate | null>(null);
  const [contributions, setContributions] = useState<TalkContribution[]>([]);
  
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pendingOpenForm, setPendingOpenForm] = useState(false);

  // Form state
  const [text, setText] = useState("");
  const [proposedQuestion, setProposedQuestion] = useState("");
  const [proposedDomain, setProposedDomain] = useState("");
  const [proposedDomainOther, setProposedDomainOther] = useState("");
  const [prefLang, setPrefLang] = useState(lang || "fr");
  const [ethicsChecked, setEthicsChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const MAX_CHARS = 250;

  useEffect(() => {
    fetchDebates();
    fetchContributions();
  }, [fetchDebates, fetchContributions]);

  useEffect(() => {
    if (allDebates.length > 0) {
      const sortedDebates = [...allDebates].sort((a,b) => {
        const dateA = new Date(a.createdAt.replace(' ', 'T')).getTime();
        const dateB = new Date(b.createdAt.replace(' ', 'T')).getTime();
        return dateB - dateA;
      });
      const active = sortedDebates.find(d => d.status === "publie") || null;
      if (active) {
        setActiveDebate(active);
        const valids = allContributions.filter(c => c.debateId === active.id && c.status === "valide");
        const chronContribs = valids.sort((a,b) => {
          const dateA = new Date(a.createdAt.replace(' ', 'T')).getTime();
          const dateB = new Date(b.createdAt.replace(' ', 'T')).getTime();
          return dateB - dateA;
        }).slice(0, 4);
        setContributions(chronContribs);
      }
    }
  }, [allDebates, allContributions]);

  useEffect(() => {
    if (isAuthenticated && pendingOpenForm) {
      setPendingOpenForm(false);
      setIsFormOpen(true);
    }
  }, [isAuthenticated, pendingOpenForm]);
  
  // Update prefLang when lang changes
  useEffect(() => {
    setPrefLang(lang);
  }, [lang]);

  const handlePrendreLaParole = () => {
    if (isAuthenticated) {
      const userHasContributed = allContributions.some(c => c.debateId === activeDebate!.id && c.authorId === user!.id);
      
      if (userHasContributed) {
        toast({
          title: t.alreadyParticipated,
          description: t.alreadyParticipatedDesc
        });
        return;
      }

      setSubmitted(false);
      setText("");
      setProposedQuestion("");
      setProposedDomain("");
      setProposedDomainOther("");
      setEthicsChecked(false);
      setError("");
      setIsFormOpen(true);
    } else {
      setPendingOpenForm(true);
      setIsAuthModalOpen(true);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_CHARS) {
      setText(e.target.value);
    }
  };

  const handleToggleLike = (id: string) => {
    const current = interactions[id];
    const newType = current === 'like' ? 'none' : 'like';
    toggleTalkContributionInteraction(id, newType);
  };

  const handleToggleDislike = (id: string) => {
    const current = interactions[id];
    const newType = current === 'dislike' ? 'none' : 'dislike';
    toggleTalkContributionInteraction(id, newType);
  };

  const getFormattedName = (name: string, category: string) => {
    if (!name || category !== 'CITOYEN') return name;
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
      return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}.`;
    }
    return name;
  };

  const handleSubmit = async () => {
    if (!text.trim()) { setError(t.errEmpty); return; }
    if (!ethicsChecked) { setError(t.errEthics); return; }

    const finalDomain = proposedDomain === "Autre" ? proposedDomainOther.trim() : proposedDomain;
    if (finalDomain && finalDomain.split(/\s+/).filter(Boolean).length > 2) {
       setError(t.errDomain);
       return;
    }

    try {
      await submitContribution({
        debateId: activeDebate!.id,
        authorId: user!.id,
        authorName: user!.name,
        authorCategory: (user!.role === "CITIZEN" ? "CITOYEN" : user!.role) as any,
        authorColor: user!.role === "ONG" ? "#10b981" : "#6366f1",
        authorShade: "clair",
        genre: user?.genre,
        authorActivity: user?.profession || user?.categorieSociopro,
        text: text.trim(),
        preferredLanguage: prefLang,
        proposedQuestion: proposedQuestion.trim() || undefined,
        proposedDomain: finalDomain || undefined
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message || "Une erreur est survenue lors de l'envoi.");
    }
  };

  if (!activeDebate) {
    return (
      <section id="kaynin-talk-section" className="py-8 bg-gradient-to-b from-background via-indigo-50/50 to-background dark:via-indigo-950/20 relative" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="container px-4 md:px-6 text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-body text-sm md:text-base font-extrabold mb-1.5 border border-indigo-200 dark:border-indigo-800">
             <MessageCircle className="w-5 h-5" /> 
             {t.talkTitle}
          </span>
          <h2 className="text-2xl md:text-3xl font-display font-extrabold text-foreground mb-4">
             {t.closedTitle}
          </h2>
          <p className="text-muted-foreground mb-6">
             {t.closedDesc}
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
             <Link to="/talk" className="inline-flex items-center gap-2 px-6 py-2.5 bg-card hover:bg-muted border border-border text-foreground rounded-2xl font-bold shadow-sm transition-all group">
                {t.allDebates}
             </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="kaynin-talk-section" className="py-8 bg-gradient-to-b from-background via-indigo-50/50 to-background dark:via-indigo-950/20 relative" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="container px-4 md:px-6">
        
        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-body text-sm md:text-base font-extrabold mb-1.5 border border-indigo-200 dark:border-indigo-800 animate-pulse">
             <MessageCircle className="w-5 h-5" /> 
             {t.talkTitle}
          </span>
          <div className="flex items-center justify-center gap-4 mb-3">
             <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-extrabold text-foreground">
                {t.subtitle}
             </h2>
          </div>
          <h3 className="text-lg md:text-xl font-body font-bold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wide mt-2">
             {t.qWeek} <span className="text-indigo-600 dark:text-indigo-400">{lang === 'ar' ? (activeDebate.titleAr || `[ترجمة آلية] ${activeDebate.title}`) : lang === 'zgh' ? (activeDebate.titleZgh || `[ⴰⵙⵓⵖⵍ ⴰⵡⵜⵓⵎⴰⵜⴰⵏ] ${activeDebate.title}`) : activeDebate.title}</span>
          </h3>
          <p className="text-sm md:text-base text-muted-foreground font-body max-w-3xl mx-auto whitespace-pre-wrap">
             {lang === 'ar' ? (activeDebate.descriptionAr || `[ترجمة آلية] ${activeDebate.description}`) : lang === 'zgh' ? (activeDebate.descriptionZgh || `[ⴰⵙⵓⵖⵍ ⴰⵡⵜⵓⵎⴰⵜⴰⵏ] ${activeDebate.description}`) : activeDebate.description}
          </p>
          
          {activeDebate.status === "cloture" && activeDebate.adminConclusion && (
            <div className="mt-4 p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl max-w-3xl mx-auto font-body text-sm text-left">
              <strong className="block mb-1 font-display">{t.adminConc}</strong>
              {activeDebate.adminConclusion}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
             {activeDebate.status !== "cloture" ? (
               <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePrendreLaParole}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-indigo-500/30 flex items-center gap-2"
               >
                  <Mic className="w-5 h-5" />
                  {t.takeFloor}
               </motion.button>
             ) : (
               <div className="bg-muted px-8 py-3 rounded-full font-bold text-muted-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t.debateClosed}
               </div>
             )}
          </div>
        </motion.div>

        {/* Grid des commentaires: max 4, 1 ligne sur desktop, 2 composants par ligne sur SM */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
           {contributions.map((cont, index) => {
              const Icon = CatIcon[cont.authorCategory] || Users;
              return (
                 <motion.div 
                    key={cont.id}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "50px" }}
                    transition={{ duration: 0.3 }}
                    className="bg-card rounded-2xl p-4 md:p-5 border shadow-sm relative overflow-hidden flex flex-col md:hover:shadow-md transition-shadow"
                    style={{ borderColor: `${cont.authorColor}40` }}
                 >
                    <div className="absolute top-0 end-0 w-16 h-16 opacity-5" style={{ backgroundColor: cont.authorColor, borderRadius: isRtl ? '0 100% 0 0' : '0 0 0 100%' }}></div>
                    <div className="flex items-center gap-2 md:gap-3 mb-4">
                       <div className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${cont.authorColor}15`, color: cont.authorColor }}>
                          <Icon className="w-4 h-4 md:w-5 md:h-5" />
                       </div>
                       <div className="min-w-0 flex-1">
                          <p className="font-bold text-xs md:text-sm text-foreground truncate" title={cont.authorName}>
                             <span dir="ltr">{getFormattedName(cont.authorName, cont.authorCategory)}</span>
                          </p>
                          <p className="text-[10px] md:text-xs text-muted-foreground capitalize truncate">
                            {cont.authorCategory === 'CITOYEN' && cont.authorActivity ? cont.authorActivity : (cont.authorCategory === 'CITOYEN' ? (lang === 'ar' ? 'مواطن(ة)' : 'Citoyen') : cont.authorCategory.toLowerCase())}
                            {cont.genre && (
                               <>
                                 <span className="mx-1">•</span>
                                 <span className={cont.genre === 'homme' ? 'text-blue-500 font-bold text-sm' : cont.genre === 'femme' ? 'text-pink-500 font-bold text-sm' : ''}>
                                   {cont.genre === 'homme' ? '♂️' : cont.genre === 'femme' ? '♀️' : ''}
                                 </span>
                               </>
                            )}
                          </p>
                       </div>
                    </div>
                    {/* Ellipsis text for long comments mapped into card */}
                    <p 
                       dir={cont.preferredLanguage === 'ar' ? 'rtl' : 'ltr'}
                       className="text-xs md:text-sm text-foreground/90 font-body mb-4 mt-auto italic line-clamp-4"
                    >
                       "{cont.text}"
                    </p>
                    <div className="flex items-center justify-between border-t border-border/50 pt-3 mt-auto">
                       <p className="text-[10px] md:text-xs text-muted-foreground">{new Date(cont.createdAt).toLocaleDateString()}</p>
                       <div className="flex items-center gap-3">
                          <button 
                             onClick={() => handleToggleLike(cont.id)}
                             className={`flex items-center gap-1 text-[10px] md:text-xs font-semibold transition-transform ${interactions[cont.id] === 'like' ? 'scale-110' : 'hover:scale-110'}`} 
                             style={{ color: interactions[cont.id] === 'like' ? cont.authorColor : 'currentColor' }}
                          >
                             <ThumbsUp className={`w-3 md:w-3.5 h-3 md:h-3.5 ${interactions[cont.id] === 'like' ? 'fill-current' : ''}`} />
                             <span>{cont.likes}</span>
                          </button>
                          <button 
                             onClick={() => handleToggleDislike(cont.id)}
                             className={`flex items-center gap-1 text-[10px] md:text-xs font-semibold transition-transform ${interactions[cont.id] === 'dislike' ? 'scale-110 text-destructive' : 'hover:scale-110 text-muted-foreground'}`}
                          >
                             <ThumbsDown className={`w-3 md:w-3.5 h-3 md:h-3.5 ${interactions[cont.id] === 'dislike' ? 'fill-current' : ''}`} />
                             <span>{cont.dislikes || 0}</span>
                          </button>
                       </div>
                    </div>
                 </motion.div>
              )
           })}
        </div>

        {/* Bouton pour consulter tous les débats */}
        <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/talk" className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-card hover:bg-muted border border-border text-foreground rounded-2xl font-bold shadow-sm transition-all group">
               {t.allComments}
               <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isRtl ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
            </Link>
        </div>
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => { setIsAuthModalOpen(false); setPendingOpenForm(false); }} />

      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(evt) => { if (evt.target === evt.currentTarget) setIsFormOpen(false); }}
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 shrink-0">
                <button onClick={() => setIsFormOpen(false)} className={`absolute top-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors ${isRtl ? 'left-4' : 'right-4'}`}>
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-white font-display font-bold text-lg">{t.formTitle}</h3>
                <p className="text-white/80 text-xs font-body mt-1">
                   {t.formDesc}
                </p>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar">
                {!submitted ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                       <label className="text-sm font-semibold">{t.prefLangLabel}</label>
                       <select value={prefLang} onChange={e => setPrefLang(e.target.value)} className="w-full h-10 rounded-xl border border-input bg-background px-3 py-2 text-sm">
                          <option value="fr">Français</option>
                          <option value="ar">العربية</option>
                          <option value="zgh">Tamazight</option>
                       </select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-end">
                         <label className="text-sm font-semibold">{t.commentLabel}</label>
                         <span className={`text-xs font-bold ${text.length === MAX_CHARS ? 'text-destructive' : 'text-muted-foreground'}`}>
                           {text.length}/{MAX_CHARS}
                         </span>
                      </div>
                      <textarea
                        value={text}
                        onChange={handleTextChange}
                        dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                        placeholder={t.commentPlaceholder}
                        className={`w-full h-32 resize-none rounded-xl border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 custom-scrollbar ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <div className="space-y-2">
                          <label className="text-sm font-semibold">{t.domainLabel}</label>
                          <select 
                             value={proposedDomain} 
                             onChange={e => setProposedDomain(e.target.value)} 
                             dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                             className={`w-full h-10 rounded-xl border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none custom-scrollbar ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                          >
                             <option value="">{t.domainSelect}</option>
                             <option value="Éducation">{t.domEducation}</option>
                             <option value="Santé">{t.domSante}</option>
                             <option value="Économie / Emploi">{t.domEco}</option>
                             <option value="Infrastructure">{t.domInfra}</option>
                             <option value="Transport">{t.domTransport}</option>
                             <option value="Environnement">{t.domEnv}</option>
                             <option value="Justice">{t.domJustice}</option>
                             <option value="Culture">{t.domCulture}</option>
                             <option value="Digitale">{t.domDigitale}</option>
                             <option value="Autre">{t.domainOther}</option>
                          </select>
                          {proposedDomain === "Autre" && (
                            <input 
                              type="text" 
                              value={proposedDomainOther}
                              onChange={e => setProposedDomainOther(e.target.value)}
                              dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                              placeholder={t.domainOtherPlaceholder}
                              className={`w-full h-10 mt-2 rounded-xl border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                            />
                          )}
                       </div>
                       <div className="space-y-2">
                          <div className="flex justify-between items-end">
                             <label className="text-sm font-semibold">{t.questionLabel}</label>
                             <span className={`text-[10px] font-bold ${proposedQuestion.length >= 150 ? 'text-destructive' : 'text-muted-foreground'}`}>{proposedQuestion.length}/150</span>
                          </div>
                          <textarea 
                             maxLength={150}
                             value={proposedQuestion}
                             onChange={e => setProposedQuestion(e.target.value)}
                             dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                             placeholder={t.questionPlaceholder}
                             className={`w-full h-20 resize-none rounded-xl border border-input bg-background p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none custom-scrollbar leading-relaxed ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                          />
                       </div>
                    </div>

                    <div className={`flex items-start gap-3 p-3 rounded-xl border ${error && !ethicsChecked ? "border-destructive bg-destructive/5" : "border-border bg-muted/30"}`}>
                      <input type="checkbox" id="ethics" checked={ethicsChecked} onChange={e => setEthicsChecked(e.target.checked)} className="mt-0.5 accent-indigo-600 w-4 h-4 shrink-0 cursor-pointer" />
                      <label htmlFor="ethics" className="text-xs text-muted-foreground cursor-pointer">
                        {t.ethics}
                      </label>
                    </div>

                    {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{error}</p>}

                    <button onClick={handleSubmit} className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2">
                      <Mic className="w-4 h-4" /> {t.publish}
                    </button>
                    <p className="text-[10px] text-center text-muted-foreground italic">
                       {t.moderation}
                    </p>
                  </div>
                ) : (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h4 className="font-display font-bold text-lg mb-2">{t.successTitle}</h4>
                    <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                      {t.successDesc}
                    </p>
                    <button onClick={() => setIsFormOpen(false)} className="px-6 py-2.5 rounded-full bg-slate-900 border text-white font-bold text-sm">
                      {t.close}
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
